/* @flow */
import { get, remove } from 'lodash';

import type {
  ReduxState,
  Action,
  Fetchable,
  SimplifiedWorklist,
  Worklist,
  WorklistItem,
} from '../../types';

// ===========================
//   STATE TYPE
// ===========================
export type WorklistsState = {
  worklists: Fetchable<Worklist[]>,
  worklist: Fetchable<Worklist>,
  worklistItems: Fetchable<WorklistItem[]>,
  selectedWorklistId?: ?number,
  worklistItemsToCheckout: WorklistItem[],
};

// ===========================
//   INITIAL STATE
// ===========================
export const initialState: WorklistsState = {
  worklists: { fetching: false },
  worklist: { fetching: false },
  worklistItems: { fetching: false },
  worklistItemsToCheckout: [],
};

// ===========================
//   REDUCER
// ===========================
export default (state: WorklistsState = initialState, action: Action): WorklistsState => {
  switch (action.type) {
    // WORKLIST_LIST
    case 'UPDATE_WL1_REQUEST':
    case 'UPDATE_WL2_REQUEST':
    case 'FETCH_WORKLIST_LIST_REQUEST': {
      return {
        ...state,
        worklists: { fetching: true },
      };
    }
    case 'UPDATE_WL1_SUCCESS':
    case 'UPDATE_WL2_SUCCESS':
    case 'FETCH_WORKLIST_LIST_SUCCESS': {
      const data = action.payload;
      return {
        ...state,
        worklists: { fetching: false, data },
      };
    }
    case 'UPDATE_WL1_FAILURE':
    case 'UPDATE_WL2_FAILURE':
    case 'FETCH_WORKLIST_LIST_FAILURE': {
      const error = action.payload;
      return {
        ...state,
        worklists: { fetching: false, error },
      };
    }
    // WORKLIST
    case 'CREATE_WORKLIST': {
      return {
        ...state,
        selectedWorklistId: null,
        worklist: { fetching: false },
      };
    }
    case 'DELETE_WORKLIST_REQUEST':
    case 'SAVE_WORKLIST_REQUEST': {
      return {
        ...state,
        worklist: { fetching: true, data: state.worklist.data },
      };
    }
    case 'FETCH_WORKLIST_REQUEST': {
      return {
        ...state,
        worklist: { fetching: true },
      };
    }
    case 'DELETE_WORKLIST_FAILURE':
    case 'SAVE_WORKLIST_FAILURE':
    case 'FETCH_WORKLIST_FAILURE': {
      const error = action.payload;
      return {
        ...state,
        worklist: { fetching: false, error },
      };
    }
    case 'FETCH_WORKLIST_SUCCESS': {
      const data = action.payload;
      return {
        ...state,
        selectedWorklistId: data.id,
        worklist: { fetching: false, data },
      };
    }
    case 'SAVE_WORKLIST_SUCCESS': {
      const data = action.payload;
      const worklistList = state.worklists.data || [];
      const isNewWorklist = !worklistList.some(worklist => worklist.id === data.id);
      return {
        ...state,
        worklists: {
          ...state.worklists,
          data: isNewWorklist
            ? [data, ...worklistList]
            : worklistList.map(worklist => (worklist.id === data.id ? data : worklist)),
        },
        worklist: { data, fetching: false },
      };
    }
    case 'DELETE_WORKLIST_SUCCESS': {
      const deletedWorklistId = action.payload;
      const isSelected = get(state.worklist, 'data.id') === action.payload;
      return {
        ...state,
        productions: {
          ...state.worklists,
          data:
            state.worklists.data &&
            remove(state.worklists.data, worklist => worklist.id !== deletedWorklistId),
        },
        worklist: isSelected ? { fetching: false } : state.worklist,
      };
    }
    case 'FETCH_WORKLIST_ITEMS_REQUEST': {
      return {
        ...state,
        worklistItems: { fetching: true },
      };
    }
    case 'FETCH_WORKLIST_ITEMS_FAILURE':
    case 'SAVE_WORKLIST_ITEMS_FAILURE': {
      const error = action.payload;
      return {
        ...state,
        worklistItems: { fetching: false, error },
      };
    }
    case 'FETCH_WORKLIST_ITEMS_SUCCESS': {
      const data = action.payload;
      return {
        ...state,
        worklistItems: { fetching: false, data },
      };
    }
    case 'SAVE_WORKLIST_ITEMS_REQUEST': {
      return {
        ...state,
        worklistItems: { ...state.worklistItems, fetching: true },
      };
    }
    case 'SAVE_WORKLIST_ITEMS_SUCCESS': {
      const savedWorklistItems = action.payload || [];
      let worklistItems = (state.worklistItems.data || []).slice();
      let isStateUpdated = false;
      savedWorklistItems.forEach(savedItem => {
        worklistItems = worklistItems.map(item => {
          if (savedItem.id === item.id && savedItem.worklistId === item.worklistId) {
            isStateUpdated = true;
            return savedItem;
          }
          return item;
        });
      });
      if (!isStateUpdated) {
        return state;
      }
      return {
        ...state,
        worklistItems: { fetching: false, data: worklistItems },
      };
    }
    case 'SAVE_WORKLIST_ITEMS_FOR_CHECKOUT': {
      const { items } = action.payload;
      return {
        ...state,
        worklistItemsToCheckout: items || [],
      };
    }
    case 'CREATE_CART_ITEMS_SUCCESS': {
      const items = action.payload;
      const worklistItemsInCart = items.map(item => item.worklistItem);
      const worklistItemsInCartIds = worklistItemsInCart.map(item => item.id);
      return {
        ...state,
        worklistItemsToCheckout: state.worklistItemsToCheckout.filter(
          item => !worklistItemsInCartIds.includes(item.id)
        ),
      };
    }
    case 'SUBMIT_QUOTATION_REQUEST_SUCCESS': {
      // TODO: update worklist items in state, when they will be cached in Redux
      // const newWorklistItems = action.payload;
      return state;
    }
    case 'LOGOUT_SUCCESS':
      return initialState;
    default: {
      return state;
    }
  }
};

// ===========================
//   SELECTORS
// ===========================

export const getWorklists = (state: ReduxState): Fetchable<Worklist[]> => {
  return state.worklists.worklists;
};

export const getSelectedWorklistId = (state: ReduxState): ?number | void => {
  return state.worklists.selectedWorklistId;
};

export const getWorklist = (state: ReduxState): Fetchable<Worklist> => {
  return state.worklists.worklist;
};

export const worklistHasPendingChanges = (state: ReduxState): boolean => {
  return state.worklists.worklist.data && false ? true : false; // TODO: remove "&& false" after testing
};

export const getWorklist1 = (state: ReduxState): Fetchable<SimplifiedWorklist> => {
  const { fetching, error, data } = state.worklists.worklists;
  return {
    fetching,
    error,
    data: data && data.find(worklist => worklist.isWorklist1),
  };
};

export const getWorklist2 = (state: ReduxState): Fetchable<SimplifiedWorklist> => {
  const { fetching, error, data } = state.worklists.worklists;
  return {
    fetching,
    error,
    data: data && data.find(worklist => worklist.isWorklist2),
  };
};

export const getWorklistItems = (state: ReduxState): Fetchable<WorklistItem[]> => {
  return state.worklists.worklistItems;
};

export const getWorklistItemsToCheckout = (state: ReduxState): WorklistItem[] => {
  return state.worklists.worklistItemsToCheckout;
};

export const getIsFetchingWorklist = (state: ReduxState): boolean => {
  return state.worklists.worklist.fetching;
};
export const getIsFetchingWorklists = (state: ReduxState): boolean => {
  return state.worklists.worklists.fetching;
};
export const getIsFetchingWorklistItems = (state: ReduxState): boolean => {
  return state.worklists.worklistItems.fetching;
};
export const getIsFetching = (state: ReduxState): boolean => {
  return (
    getIsFetchingWorklist(state) ||
    getIsFetchingWorklists(state) ||
    getIsFetchingWorklistItems(state)
  );
};
